import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowIcon, CheckCircleIcon, SearchIcon } from "asset/icons";

export const PhoneCountryDropdown = ({ value, options, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const selectedCountry = options.find((option) => option.code === value);
  const filteredOptions = options.filter((option) => {
    const query = search.trim().toLowerCase();
    return (
      option.name.toLowerCase().includes(query) ||
      option.code.toLowerCase().includes(query) ||
      option.dialCode.includes(query)
    );
  });

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const viewportPadding = 16;
    const menuGap = 8;
    const menuWidth = Math.min(Math.max(rect.width, 300), viewportWidth - viewportPadding * 2);
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      viewportWidth - menuWidth - viewportPadding
    );
    const desiredHeight = Math.min(Math.max(filteredOptions.length * 48 + 84, 180), 360);
    const spaceBelow = viewportHeight - rect.bottom - viewportPadding - menuGap;
    const spaceAbove = rect.top - viewportPadding - menuGap;
    const shouldOpenUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
    const availableHeight = Math.max(shouldOpenUpward ? spaceAbove : spaceBelow, 120);
    const maxHeight = Math.min(desiredHeight, availableHeight);

    setMenuStyle({
      left,
      maxHeight,
      top: shouldOpenUpward
        ? Math.max(viewportPadding, rect.top - menuGap - maxHeight)
        : Math.min(rect.bottom + menuGap, viewportHeight - viewportPadding),
      width: menuWidth,
    });
  }, [filteredOptions.length]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger = wrapperRef.current?.contains(event.target);
      const clickedMenu = menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedMenu) setOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        style={menuStyle}
        className="fixed z-[10000] overflow-hidden rounded-xl border border-[#E6E8F9] bg-white p-3 shadow-[-16px_-16px_16px_-4px_#10182808,16px_16px_32px_-4px_#10182814]"
      >
        <div className="flex h-10 items-center gap-3 rounded-lg bg-[#F4F2FC] px-4">
          <SearchIcon className="size-5 *:stroke-[#6F739B]" />
          <input
            autoFocus
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
            className="aku-body-normal-regular h-full w-full bg-transparent text-[#101828] outline-none placeholder:text-[#6F739B]"
          />
        </div>

        <div
          className="mt-3 overflow-y-auto pr-1"
          style={{ maxHeight: Math.max((menuStyle.maxHeight || 180) - 64, 96) }}
        >
          {filteredOptions.map((country) => {
            const active = country.code === value;

            return (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  onChange(country.code);
                  setSearch("");
                  setOpen(false);
                }}
                className={`aku-body-normal-medium flex h-12 w-full items-center gap-3 rounded-lg px-2 text-left text-[#101828] transition-colors ${
                  active ? "bg-[#F4F2FC]" : "hover:bg-[#F8F7FF]"
                }`}
              >
                <span className="text-xl leading-none">{country.flag}</span>
                <span className="min-w-0 flex-1 truncate">{country.name}</span>
                <span className="text-sm text-[#6F739B]">{country.dialCode}</span>
                {active && <CheckCircleIcon className="size-5 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>,
      document.body
    );

  return (
    <div ref={wrapperRef} className="h-full">
      <button
        type="button"
        disabled={loading}
        onClick={() => !loading && setOpen((prev) => !prev)}
        className="aku-body-normal-regular flex h-full min-w-[138px] items-center justify-between gap-3 rounded-lg bg-[#f1f5f9] px-4 text-[#101828] disabled:cursor-not-allowed disabled:opacity-70"
      >
        <span className="flex items-center gap-2">
          <span className="text-xl leading-none">{selectedCountry?.flag}</span>
          <span>{selectedCountry?.code || "--"}</span>
        </span>
        <ArrowIcon className="size-3.5 shrink-0 *:stroke-[#6F739B]" />
      </button>
      {menu}
    </div>
  );
};

export default PhoneCountryDropdown;
